// These three live in locales.js, which is CommonJS so next.config.js can
// require the same values -- see the comment there. Everything else in this file
// is typed against the union they produce.
import { LOCALES, DEFAULT_LOCALE, LOCALE_COOKIE } from "./locales";

export type Locale = (typeof LOCALES)[number];

/** Next's convention. Its built-in detection reads the same name, and so does
 * the `has` condition on the returning-visitor redirects in next.config.js. */
export { LOCALES, DEFAULT_LOCALE, LOCALE_COOKIE };

/** One year. The choice is an explicit user preference, so it should outlive a session. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** For `og:locale`. es_US, not es_ES -- the audience is Spanish speakers in the
 * United States, not readers in Spain. */
export const OG_LOCALES: Record<Locale, string> = {
  en: "en_US",
  es: "es_US",
};

/**
 * Locale tags for `Intl.*` formatting, kept separate from the routing locale.
 *
 * The routing locale has to stay the bare "es" -- it is the URL prefix, and
 * Google only accepts a language plus an ISO 3166-1 region in hreflang, so
 * neither "es-419" nor a rename to "/es-US/" is on the table. But bare "es"
 * makes Intl fall back to Spain's conventions: 6.132.096 and a rating of 4,8.
 * Readers in the US expect 6,132,096 and 4.8, so formatting resolves through
 * here instead.
 */
export const INTL_LOCALES: Record<Locale, string> = {
  en: "en-US",
  es: "es-US",
};

export function getIntlLocale(locale: Locale): string {
  return INTL_LOCALES[locale];
}

/**
 * Language names written in their own language, for the switcher.
 *
 * Written out rather than taken from `Intl.DisplayNames`. CLDR is the right
 * source for the 184 languages in the lawyer data, but it renders Spanish as
 * "español" -- correct prose, wrong for a picker -- and for a handful of UI
 * locales a table costs nothing and keeps control of capitalisation.
 * `Record<Locale, string>` means a new locale cannot skip its label.
 */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
};

export function isLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" && (LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Narrows whatever `useRouter().locale` hands us. It is typed `string | undefined`
 * and is undefined when i18n is disabled, so every read needs a fallback.
 */
export function toLocale(value: unknown): Locale {
  return isLocale(value) ? value : DEFAULT_LOCALE;
}

/**
 * Builds the public URL path for a route in a given locale. The default locale
 * is prefixless -- that is the whole point of the sub-path setup, and existing
 * English URLs must not change.
 */
export function localizePathname(pathname: string, locale: Locale): string {
  const normalized = pathname === "" ? "/" : pathname;
  if (locale === DEFAULT_LOCALE) return normalized;
  return normalized === "/" ? `/${locale}` : `/${locale}${normalized}`;
}
